<template>
  <div v-if="(role == 'financial' || role == 'admin' || role == 'sales') && order.order_state != 'cancel'" 
       class="bg-white shadow-sm rounded-lg overflow-hidden mb-6">
    <div class="p-6">
      <h2 class="text-lg font-medium text-gray-900 mb-6">Registrar Pago (Multi-Moneda)</h2>
      
      <!-- Currency & Payment Method Selection Form -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
        <!-- Currency Selector -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Moneda de Pago</label>
          <div class="flex items-center space-x-4 mt-1">
            <label class="inline-flex items-center cursor-pointer">
              <input 
                type="radio" 
                v-model="selectedCurrency" 
                value="Bs" 
                class="form-radio h-4 w-4 text-indigo-600 focus:ring-indigo-500" 
                @change="onCurrencyChange"
              />
              <span class="ml-2 text-sm text-gray-900 font-medium">Bolívares (Bs)</span>
            </label>
            <label class="inline-flex items-center cursor-pointer">
              <input 
                type="radio" 
                v-model="selectedCurrency" 
                value="USD" 
                class="form-radio h-4 w-4 text-indigo-600 focus:ring-indigo-500" 
                @change="onCurrencyChange"
              />
              <span class="ml-2 text-sm text-gray-900 font-medium">Dólares (USD $)</span>
            </label>
          </div>
        </div>

        <!-- Payment Method Selector -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Método de pago</label>
          <select 
            v-model="form.method" 
            class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            @change="onMethodChange"
          >
            <option value="">Seleccionar método</option>
            <option 
              v-for="pm in filteredPaymentMethods" 
              :key="pm.id" 
              :value="pm.name"
            >
              {{ pm.name }} ({{ pm.currency ? pm.currency.alias : selectedCurrency }})
            </option>
          </select>
        </div>

        <!-- Exchange Rate Info / Rate Box -->
        <div class="flex items-center bg-gray-50 p-3 rounded-md text-sm text-gray-600 border border-gray-200">
          <svg class="h-5 w-5 text-indigo-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <div>
            <span class="font-medium text-gray-800">Tasa del sistema:</span> 1 USD = {{ change }} Bs
          </div>
        </div>
      </div>

      <!-- Amounts, Custom Rate, Fecha de Pago & Reference Details -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-4">
        <!-- Amount in Bs -->
        <div>
          <text-input 
            v-model="amountBs" 
            type="number" 
            step="0.01" 
            :disabled="selectedCurrency !== 'Bs'"
            :error="form.errors.amount_bs" 
            class="w-full" 
            label="Monto en Bs." 
            @update:modelValue="onAmountBsInput"
          />
        </div>

        <!-- Custom Rate (Editable when Bs, fixed/info when USD) -->
        <div>
          <text-input 
            v-model="form.change" 
            type="number" 
            step="0.0001" 
            :disabled="selectedCurrency !== 'Bs'"
            :error="form.errors.change" 
            class="w-full" 
            :label="selectedCurrency === 'Bs' ? 'Tasa del Pago (Bs / $)' : 'Tasa Equivalente'" 
            @update:modelValue="onChangeInput"
          />
        </div>

        <!-- Amount in USD -->
        <div>
          <text-input 
            v-model="form.amount" 
            type="number" 
            step="0.01" 
            :error="form.errors.amount" 
            class="w-full" 
            :label="selectedCurrency === 'Bs' ? 'Monto equiv. ($ USD)' : 'Monto ($ USD)'" 
            @update:modelValue="onAmountUsdInput"
          />
        </div>

        <!-- Fecha del Pago -->
        <div>
          <text-input 
            v-model="form.payment_date" 
            type="date" 
            :error="form.errors.payment_date" 
            class="w-full" 
            label="Fecha del Pago" 
          />
        </div>
      </div>

      <!-- Reference Number & Pago Móvil Issuer Details -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-4" v-if="requiresReference || isPagoMovil">
        <!-- Reference Number -->
        <div v-if="requiresReference">
          <text-input 
            v-model="form.reference" 
            :error="form.errors.reference" 
            class="w-full" 
            label="Número de Referencia" 
          />
        </div>

        <template v-if="isPagoMovil">
          <div>
            <text-input 
              v-model="form.issuer_phone" 
              :error="form.errors.issuer_phone" 
              class="w-full" 
              label="Teléfono del Emisor" 
              placeholder="Ej: 04125394042"
            />
          </div>

          <div>
            <text-input 
              v-model="form.issuer_id" 
              :error="form.errors.issuer_id" 
              class="w-full" 
              label="Cédula / RIF Emisor" 
              placeholder="Ej: V-408616874"
            />
          </div>

          <div>
            <text-input 
              v-model="form.issuer_bank" 
              :error="form.errors.issuer_bank" 
              class="w-full" 
              label="Banco Emisor / Origen" 
              placeholder="Ej: 0191 - BNC / Mercantil"
            />
          </div>
        </template>
      </div>

      <!-- Submit Payment Button -->
      <div class="flex justify-end mt-4">
        <button 
          @click="addPay" 
          :disabled="form.processing"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200 shadow-sm"
        >
          <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Registrar pago
        </button>
      </div>

      <!-- Payments Table -->
      <div class="mt-8">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Historial de Pagos</h3>
        <div class="overflow-x-auto border border-gray-200 rounded-lg">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Método</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto ($ USD)</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto (Bs)</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tasa Pago</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Pago</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referencia</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="pay in order.pays" :key="pay.id" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium capitalize">
                  {{ pay.method }}
                  <div v-if="pay.issuer_bank || pay.issuer_phone || pay.issuer_id" class="text-xs text-gray-400 font-normal">
                    <span v-if="pay.issuer_bank">{{ pay.issuer_bank }} </span>
                    <span v-if="pay.issuer_phone">Tél: {{ pay.issuer_phone }} </span>
                    <span v-if="pay.issuer_id">CI: {{ pay.issuer_id }}</span>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                  ${{ Number(pay.amount).toFixed(2) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  <span v-if="editingPayId === pay.id" class="font-semibold text-indigo-700">
                    Bs. {{ editForm.amount_bs }}
                  </span>
                  <span v-else>
                    Bs. {{ (pay.amount_bs ? Number(pay.amount_bs) : Number(pay.amount * (pay.change || change))).toFixed(2) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div v-if="editingPayId === pay.id" class="flex items-center space-x-2">
                    <input 
                      type="number" 
                      step="0.0001" 
                      v-model="editForm.change" 
                      @input="onEditChangeInput(pay, $event.target.value)"
                      class="w-28 px-2 py-1 border border-indigo-400 rounded-md text-xs font-semibold focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                    />
                  </div>
                  <div v-else class="flex items-center space-x-1.5">
                    <span>{{ pay.change ? Number(pay.change).toFixed(4) : Number(change).toFixed(4) }}</span>
                    <button 
                      @click="startEditRate(pay)" 
                      class="text-gray-400 hover:text-indigo-600 p-0.5 rounded transition-colors"
                      title="Modificar Tasa"
                    >
                      <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div v-if="editingPayId === pay.id">
                    <input 
                      type="date" 
                      v-model="editForm.payment_date" 
                      class="px-2 py-1 border border-indigo-400 rounded-md text-xs font-semibold focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                    />
                  </div>
                  <span v-else>{{ formatDate(pay.payment_date || pay.created_at) }}</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ pay.reference || '-' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500"> 
                  <div v-if="editingPayId === pay.id" class="flex items-center space-x-2">
                    <button 
                      @click="saveRate(pay)" 
                      :disabled="editForm.processing"
                      class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
                    >
                      Guardar
                    </button>
                    <button 
                      @click="cancelEditRate" 
                      class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                    >
                      Cancelar
                    </button>
                  </div>
                  <div v-else class="flex items-center space-x-2">
                    <button 
                      @click="startEditRate(pay)" 
                      class="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none transition-colors duration-200"
                      title="Editar Tasa de Pago"
                    >
                      <svg class="h-3.5 w-3.5 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      Editar
                    </button>
                    <a 
                      target="_blank" 
                      :href="`/orders/pay/${pay.id}/pdf`" 
                      class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                    >
                      <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      Recibo
                    </a>
                    <button 
                      @click="deletePay(pay.id)" 
                      class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                    >
                      <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Anular
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="!order.pays || order.pays.length === 0">
                <td colspan="7" class="px-6 py-4 text-center text-sm text-gray-500">
                  No hay pagos registrados para esta cuota.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Approve payments action -->
      <div v-if="order.order_state == 'payRevition'" class="mt-6">
        <button 
          @click="orderFinish()" 
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200 shadow-sm"
        >
          <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
          Aprobar pagos
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import TextInput from '@/Shared/TextInput.vue'

export default {
  name: 'Payment',
  components: {
    TextInput,
  },
  props: {
    order: {
      type: Object,
      required: true
    },
    paymentMethods: {
      type: Array,
      default: () => []
    },
    change: {
      type: Number,
      default: 1
    },
    role: {
      type: String,
      default: ''
    }
  },
  data() {
    const today = new Date().toISOString().substr(0, 10)
    return {
      selectedCurrency: 'Bs',
      amountBs: 0,
      editingPayId: null,
      editForm: this.$inertia.form({
        change: 0,
        amount_bs: 0,
        payment_date: today,
        issuer_bank: '',
        issuer_phone: '',
        issuer_id: '',
        issuer_name: ''
      }),
      form: this.$inertia.form({
        amount: 0,
        amount_bs: 0,
        change: this.change,
        method: '',
        order_id: this.order.id,
        reference: '',
        payment_date: today,
        issuer_bank: '',
        issuer_phone: '',
        issuer_id: '',
        issuer_name: '',
        status: 'approved',
        voucher: ''
      }),
    }
  },
  computed: {
    filteredPaymentMethods() {
      if (!this.paymentMethods || this.paymentMethods.length === 0) {
        if (this.selectedCurrency === 'Bs') {
          return [
            { id: 1, name: 'Pago móvil', currency: { alias: 'Bs' } },
            { id: 2, name: 'Transferencia', currency: { alias: 'Bs' } },
            { id: 3, name: 'Efectivo', currency: { alias: 'Bs' } }
          ]
        } else {
          return [
            { id: 4, name: 'Zelle', currency: { alias: 'USD' } },
            { id: 5, name: 'Efectivo', currency: { alias: 'USD' } },
            { id: 6, name: 'Binance', currency: { alias: 'USD' } }
          ]
        }
      }

      return this.paymentMethods.filter(pm => {
        const alias = pm.currency ? pm.currency.alias : ''
        return alias.toUpperCase() === this.selectedCurrency.toUpperCase()
      })
    },
    requiresReference() {
      const m = (this.form.method || '').toLowerCase()
      return m.includes('pago móvil') || m.includes('pago movil') || m.includes('transferencia') || m.includes('zelle') || m.includes('binance')
    },
    isPagoMovil() {
      const m = (this.form.method || '').toLowerCase()
      return m.includes('pago móvil') || m.includes('pago movil') || m.includes('pagomovil') || m.includes('transferencia')
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const cleanDate = dateString.replace('T', ' ').split(' ')[0];
        const parts = cleanDate.split('-');
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateString;
      } catch (e) {
        return dateString;
      }
    },
    startEditRate(pay) {
      this.editingPayId = pay.id
      const currentRate = pay.change ? parseFloat(pay.change) : this.change
      const calculatedBs = pay.amount_bs ? parseFloat(pay.amount_bs) : Number((pay.amount * currentRate).toFixed(2))
      const payDate = pay.payment_date ? pay.payment_date : (pay.created_at ? pay.created_at.substr(0, 10) : new Date().toISOString().substr(0, 10))
      this.editForm = this.$inertia.form({
        change: currentRate,
        amount_bs: calculatedBs,
        payment_date: payDate,
        issuer_bank: pay.issuer_bank || '',
        issuer_phone: pay.issuer_phone || '',
        issuer_id: pay.issuer_id || '',
        issuer_name: pay.issuer_name || ''
      })
    },
    cancelEditRate() {
      this.editingPayId = null
    },
    onEditChangeInput(pay, val) {
      const newRate = parseFloat(val) || 0
      this.editForm.change = newRate
      if (pay.amount && newRate > 0) {
        this.editForm.amount_bs = Number((pay.amount * newRate).toFixed(2))
      }
    },
    saveRate(pay) {
      if (!this.editForm.change || this.editForm.change <= 0) {
        alert('Ingrese una tasa válida mayor a 0')
        return
      }
      this.editForm.put(`/orders/pay/${pay.id}`, {
        preserveScroll: true,
        onSuccess: () => {
          this.editingPayId = null
        }
      })
    },
    onCurrencyChange() {
      this.form.method = ''
      this.form.amount = 0
      this.form.amount_bs = 0
      this.form.change = this.change
      this.amountBs = 0
      this.form.reference = ''
    },
    onMethodChange() {
      this.form.reference = ''
    },
    onAmountBsInput(val) {
      const valBs = parseFloat(val) || 0
      this.amountBs = valBs
      this.form.amount_bs = valBs
      const currentRate = parseFloat(this.form.change) || this.change || 1
      this.form.amount = currentRate > 0 ? Number((valBs / currentRate).toFixed(2)) : 0
    },
    onChangeInput(val) {
      const currentRate = parseFloat(val) || 0
      this.form.change = currentRate
      if (this.selectedCurrency === 'Bs') {
        const valBs = parseFloat(this.amountBs) || 0
        this.form.amount = currentRate > 0 ? Number((valBs / currentRate).toFixed(2)) : 0
      } else {
        const valUsd = parseFloat(this.form.amount) || 0
        this.form.amount_bs = Number((valUsd * currentRate).toFixed(2))
        this.amountBs = this.form.amount_bs
      }
    },
    onAmountUsdInput(val) {
      const valUsd = parseFloat(val) || 0
      this.form.amount = valUsd
      const currentRate = parseFloat(this.form.change) || this.change || 1
      this.form.amount_bs = Number((valUsd * currentRate).toFixed(2))
      this.amountBs = this.form.amount_bs
    },
    orderFinish() {
      this.$inertia.get('/order/' + this.order.id + '/finish')
    },
    addPay() {
      if (!this.form.method) {
        alert('Por favor seleccione un método de pago')
        return
      }

      if (this.requiresReference && !this.form.reference) {
        alert('Debe ingresar el número de referencia para este método de pago')
        return
      }

      const payAmountUsd = parseFloat(this.form.amount) || 0
      if (payAmountUsd <= 0) {
        alert('El monto del pago debe ser mayor a cero')
        return
      }

      const currentBalance = parseFloat(this.order.balance) || 0
      const totalOrder = parseFloat(this.order.total) || 0

      if (totalOrder < payAmountUsd + currentBalance) {
        alert('No se puede agregar este pago porque el monto supera el total de la cuota')
        return
      }

      this.form.order_id = this.order.id
      this.form.post('/orders/pay', {
        onSuccess: () => {
          this.cleanPay()
        }
      })
    },
    deletePay(id) {
      if (confirm('¿Estás seguro que quieres anular este pago? Esta acción modificará el balance de la orden.')) {
        this.$inertia.delete(`/orders/pay/${id}`)
      }
    },
    cleanPay() {
      const today = new Date().toISOString().substr(0, 10)
      this.amountBs = 0
      this.form = this.$inertia.form({
        amount: 0,
        amount_bs: 0,
        change: this.change,
        method: '',
        order_id: this.order.id,
        reference: '',
        payment_date: today,
        issuer_bank: '',
        issuer_phone: '',
        issuer_id: '',
        issuer_name: '',
        status: 'approved',
        voucher: ''
      })
    }
  }
}
</script>
